import { FC } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { logButtonEvent } from 'app/amplitude';
import { IconCheck, IconTimes, PrimaryBooking, NonPrimaryBooking } from 'icons';
import { Tag } from 'ui';
import { AllBookingsForGuardianType, BookingType, Status } from 'graphql/generated-types';

import {
  formatSessionType,
  getBookingDetailsHref,
  mapBookingStatusToTagType,
  mapBookingStatusToText,
} from './utils';
import styles from './SessionCard.module.scss';

interface IBookingTypeIconProps {
  bookingType: BookingType;
}

const BookingTypeIcon: FC<IBookingTypeIconProps> = ({ bookingType }) => {
  switch (bookingType) {
    case BookingType.SessionNonPrimary:
      return (
        <NonPrimaryBooking
          className={styles.detailIcon}
          size={15}
          aria-label="Non-Primary booking icon"
          focusable="false"
        />
      );
    case BookingType.SessionPrimary:
      return (
        <PrimaryBooking
          className={styles.detailIcon}
          size={15}
          aria-label="Primary booking icon"
          focusable="false"
        />
      );
    default:
      return null;
  }
};
const trackClick = (bookingType: BookingType) => {
  switch (bookingType) {
    case BookingType.SessionNonPrimary:
      logButtonEvent('nonprimary_confirmation_selected');
      return;
    case BookingType.SessionPrimary:
      logButtonEvent('session_confirmation_selected');
      return;
    default:
      return;
  }
};

interface ISessionCardProps {
  session: AllBookingsForGuardianType;
  showChildNames: boolean;
}

export const SessionCard: FC<ISessionCardProps> = ({ session, showChildNames, ...props }) => {
  const sessionDetailsHref = getBookingDetailsHref(session);
  const sessionStatus = session.status;
  const showCheckmark = sessionStatus === Status.Confirmed;
  const showCross = sessionStatus === Status.Cancelled || sessionStatus === Status.Rejected;

  return (
    <Link
      onClick={() => trackClick(session.bookingType as BookingType)}
      className={styles.container}
      to={sessionDetailsHref}
      {...props}
    >
      <div className={styles.header}>
        {/* session.bookingType is still return as Scalars['String'] by API */}
        <div className={styles.info}>
          <BookingTypeIcon bookingType={session.bookingType as BookingType} />
          <div className={styles.sessionDetails}>
            {format(new Date(session.date), 'E, do MMM.')}
          </div>
          <div className={styles.sessionType}>{formatSessionType(session, false)}</div>
        </div>
        <div className={styles.status}>
          <Tag type={mapBookingStatusToTagType(sessionStatus)} className={styles.statusLabel}>
            {showCheckmark && <IconCheck className={styles.tagIcon} size={10} />}
            {showCross && <IconTimes className={styles.tagIcon} size={12} />}
            {mapBookingStatusToText(sessionStatus)}
          </Tag>
        </div>
      </div>

      {showChildNames && session.child && (
        <Tag className={styles.child}>{session.child.fullName.split(' ')[0]}</Tag>
      )}
    </Link>
  );
};
